import { z } from 'zod';
import { EmailSchema, PhoneSchema, TimestampsSchema } from './common.js';
import { PrimaryContactMethodEnum, UserTypeEnum } from './auth.js';
// =============================================================================
// User Schema - Global identity across organizations
// =============================================================================
// REVISED per Deep Architect Review:
// 1. Email and phone are OPTIONAL (at least one required, validated in pre-save)
// 2. Removed loginCodes[] - moved to auth_challenges collection
// 3. Added emailVerified, phoneVerified, primaryContactMethod
// =============================================================================
/**
 * DocuSign OAuth tokens stored per-user (NOT per-org)
 */
export const DocuSignTokensSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresAt: z.coerce.date(),
    basePath: z.string().url(),
    accountId: z.string(),
});
/**
 * Full User schema (database document)
 * Note: At least one of email or phone must be present (validated in pre-save hook)
 */
export const UserSchema = z
    .object({
    _id: z.string(),
    // Identity - At least one required (validated in pre-save hook)
    email: EmailSchema.optional(),
    phone: PhoneSchema.optional(),
    // Profile
    firstName: z.string().min(1, 'First name is required').max(50),
    lastName: z.string().min(1, 'Last name is required').max(50),
    avatarUrl: z.string().optional(),
    // Professional info (optional)
    companyName: z.string().max(100).optional(),
    licenseNumber: z.string().max(50).optional(),
    // Verification Status
    emailVerified: z.boolean().default(false),
    phoneVerified: z.boolean().default(false),
    // Primary Contact Method (for notifications)
    primaryContactMethod: PrimaryContactMethodEnum,
    // User Type
    userType: UserTypeEnum.default('AGENT'),
    // Account status
    isActive: z.boolean().default(true),
    lastLoggedIn: z.coerce.date().optional(),
    // DocuSign OAuth (stored per-user, NOT per-org)
    docusign: DocuSignTokensSchema.optional(),
})
    .merge(TimestampsSchema)
    .refine((data) => data.email !== undefined || data.phone !== undefined, { message: 'At least one contact method (email or phone) is required' });
/**
 * User creation input (signup)
 * At least one of email or phone is required
 */
export const CreateUserInputSchema = z.object({
    firstName: z.string().min(1, 'First name is required').max(50),
    lastName: z.string().min(1, 'Last name is required').max(50),
    email: EmailSchema.optional(),
    phone: PhoneSchema.optional(),
    primaryContactMethod: PrimaryContactMethodEnum,
    companyName: z.string().max(100).optional(),
    licenseNumber: z.string().max(50).optional(),
}).refine((data) => data.email !== undefined || data.phone !== undefined, { message: 'At least one contact method (email or phone) is required' }).refine((data) => {
    // Validate primary contact method matches available contact
    if (data.primaryContactMethod === 'email' && !data.email)
        return false;
    if (data.primaryContactMethod === 'phone' && !data.phone)
        return false;
    return true;
}, { message: 'Primary contact method must match an available contact method' });
/**
 * User update input
 */
export const UpdateUserInputSchema = z.object({
    firstName: z.string().min(1).max(50).optional(),
    lastName: z.string().min(1).max(50).optional(),
    avatarUrl: z.string().nullable().optional(),
    companyName: z.string().min(1).max(100).nullable().optional(),
    licenseNumber: z.string().min(1).max(50).nullable().optional(),
    phone: PhoneSchema.optional(),
    primaryContactMethod: PrimaryContactMethodEnum.optional(),
});
/**
 * Profile update input (for PATCH /auth/me)
 * Omits primaryContactMethod — profile edits should not change notification preference
 */
export const UpdateProfileSchema = UpdateUserInputSchema.omit({ primaryContactMethod: true });
/**
 * User response (safe to send to client - excludes sensitive fields)
 */
export const UserResponseSchema = z.object({
    _id: z.string(),
    email: EmailSchema.optional(),
    phone: PhoneSchema.optional(),
    firstName: z.string(),
    lastName: z.string(),
    avatarUrl: z.string().optional(),
    companyName: z.string().optional(),
    licenseNumber: z.string().optional(),
    emailVerified: z.boolean(),
    phoneVerified: z.boolean(),
    primaryContactMethod: PrimaryContactMethodEnum,
    userType: UserTypeEnum,
    isActive: z.boolean(),
    lastLoggedIn: z.coerce.date().optional(),
    hasDocuSignConnected: z.boolean(),
    // Password auth v1 (2026-09): derived flags only — the password hash
    // itself must NEVER appear in any client-facing schema.
    hasPassword: z.boolean(),
    mfaEnabled: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// =============================================================================
// DEPRECATED: Old Auth Schemas (kept for backward compatibility during migration)
// These will be removed after migration to new auth system is complete
// Use schemas from ./auth.ts instead
// =============================================================================
/**
 * @deprecated Use SendCodeRequestSchema from ./auth.ts
 * Login request (send code) - phone only
 */
export const LoginRequestSchema = z.object({
    phone: PhoneSchema,
});
/**
 * @deprecated Use VerifyCodeRequestSchema from ./auth.ts
 * Verify code request - phone only
 */
export const VerifyCodeRequestSchema = z.object({
    phone: PhoneSchema,
    code: z.string().length(6, 'Code must be 6 digits'),
});
// =============================================================================
// REMOVED: LoginCodeSchema
// Login codes are now stored in the auth_challenges collection
// with Argon2 hashing. See auth.ts for AuthChallengeSchema.
// =============================================================================
//# sourceMappingURL=user.js.map